import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createTask, uploadImage, uploadFile, getImageUrl, aiParseInstructions, getExecutors, getNextKsDesignNo } from '@/utils/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ClientSelect } from '@/lib/ClientSelect';
import CollapsibleLayout from '@/components/CollapsibleLayout';
import { TaskStoneImportDialog } from '@/components/TaskStoneImportDialog';
import { StyleNoPromptDialog } from '@/components/StyleNoPromptDialog';
import {
  Save, X, Upload, Plus, Trash2, ArrowLeft, RotateCcw, Grid, Sparkles, Loader2
} from 'lucide-react';
import {
  PIECE_TYPES,
  METAL_TYPES,
  METAL_COLOURS,
  METAL_KARATS,
  STONE_SHAPES,
  SETTING_TYPES,
  FINDINGS_BY_PIECE_TYPE,
  PRIORITY_LEVELS,
  CAD_DIFFICULTY_LEVELS,
  STAGES,
} from '@/utils/constants';
import { FINISHING_TYPES } from '@/utils/cad-sheet';

const SECTIONS = [
  { id: 'basic', label: 'Basic Info' },
  { id: 'metal', label: 'Metal & Weights' },
  { id: 'stones', label: 'Stone Details' },
  { id: 'findings', label: 'Findings' },
  { id: 'finishing', label: 'Finishing & Engraving' },
  { id: 'images', label: 'Images' },
  { id: 'other', label: 'Other Details' },
  { id: 'workflow', label: 'Workflow & Notes' },
];

const CreateTaskPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isExecutorOrFinalExecutor = user?.role === 'executor' || user?.role === 'final_executor';
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [bulkStoneOpen, setBulkStoneOpen] = useState(false);
  const sectionRefs = useRef({});
  const [executors, setExecutors] = useState([]);

  // Fetch executors for the designer dropdown
  useEffect(() => {
    getExecutors()
      .then(res => setExecutors(res.data || []))
      .catch(() => setExecutors([]));
  }, []);

  // Fetch KS Design No for customer portal on load
  useEffect(() => {
    if (user?.role === 'customer') {
      const clientCode = user?.client_code || user?.company_name || user?.name;
      if (clientCode) {
        getNextKsDesignNo(clientCode)
          .then(res => {
            setFormData(p => ({ ...p, ks_design_no: res.data.next_no }));
          })
          .catch(() => {
            toast.error('Failed to generate KS Design No');
          });
      }
    }
  }, [user]);

  // Metals array (multi-metal like CAD Sheet)
  const [metals, setMetals] = useState([
    { id: crypto.randomUUID(), karat: '14K', color: 'Yellow', metalType: 'gold', netWeight: '' }
  ]);
  const [grossWeightManual, setGrossWeightManual] = useState(false);

  const [formData, setFormData] = useState({
    party_cad_no: '',
    style_no: '',
    ks_design_no: '',
    client_name: user?.role === 'customer' ? (user?.client_code || user?.company_name || user?.name) : '',
    product_name: '',
    piece_type: 'ring',
    size: '',
    quantity: '',
    priority: 'normal',
    deadline: '',
    collection_name: '',
    cad_file_name: '',
    designer_name: '',
    designer_hashtag: '',
    designer_id: '',
    design_points: '',
    cad_url: '',
    net_weight: '',
    gross_weight: '',
    // CAD Points
    cad_difficulty: 'moderate',
    cad_points: '',
    cad_start_time: '',
    finishing: '',
    rhodium_plating: '',
    engraving: '',
    special_instructions: '',
    stamping_instructions: '',
    chain_assembling: '',
    other_details: '',
    stage: '',
    start_date: new Date().toISOString().split('T')[0],
    target_date: '',
    notes: '',
    product_image: '',
    cad_file: '',
    reference_images: [],
    stamping_images: [],
  });

  const [stones, setStones] = useState([]);
  const [findings, setFindings] = useState([]);

  // AI Instruction Box
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiParsing, setAiParsing] = useState(false);
  const [showAiBox, setShowAiBox] = useState(false);

  const handleAiParse = async () => {
    if (!aiInstruction.trim()) { toast.error('Enter instructions to parse'); return; }
    setAiParsing(true);
    try {
      const res = await aiParseInstructions(aiInstruction);
      const parsed = res.data;
      let fieldsApplied = 0;

      // Only fill fields that are currently empty (don't overwrite user data)
      setFormData(prev => {
        const updated = { ...prev };
        const textFields = ['client_name', 'party_cad_no', 'style_no', 'product_name', 'piece_type',
          'category', 'collection_name', 'size', 'gender',
          'rhodium_plating', 'engraving', 'finishing', 'special_instructions',
          'designer_name', 'notes'];

        for (const field of textFields) {
          if (parsed[field] && !prev[field]) {
            updated[field] = parsed[field];
            fieldsApplied++;
          }
        }
        // Priority — override default 'medium' if AI suggests differently
        if (parsed.priority && parsed.priority !== prev.priority) {
          updated.priority = parsed.priority;
          fieldsApplied++;
        }
        // Quantity
        if (parsed.quantity && !prev.quantity) {
          updated.quantity = String(parsed.quantity);
          fieldsApplied++;
        }
        // Weights
        if (parsed.net_weight && !prev.net_weight) { updated.net_weight = String(parsed.net_weight); fieldsApplied++; }
        if (parsed.gross_weight && !prev.gross_weight) { updated.gross_weight = String(parsed.gross_weight); fieldsApplied++; }
        if (parsed.target_weight && !prev.target_weight) { updated.target_weight = String(parsed.target_weight); fieldsApplied++; }

        return updated;
      });

      // Metal from parsed
      if (parsed.metal_type || parsed.metal_karat || parsed.metal_colour) {
        setMetals(prev => {
          const first = prev[0] || {};
          const updated = { ...first };
          if (parsed.metal_type && !first.metalType) { updated.metalType = parsed.metal_type; fieldsApplied++; }
          if (parsed.metal_karat && first.karat === '14K') { updated.karat = parsed.metal_karat; fieldsApplied++; }
          if (parsed.metal_colour && first.color === 'Yellow') { updated.color = parsed.metal_colour; fieldsApplied++; }
          if (parsed.net_weight && !first.netWeight) { updated.netWeight = String(parsed.net_weight); }
          return [updated, ...prev.slice(1)];
        });
      }

      // Diamonds
      if (parsed.diamonds?.length > 0 && stones.length === 0) {
        const newStones = parsed.diamonds.map(d => ({
          id: crypto.randomUUID(),
          category: d.category || 'Accent Stone',
          stoneCategory: d.stone_category || 'Natural',
          shape: d.stone_shape || 'Round',
          quality: d.stone_quality || '',
          qty: d.quantity || 1,
          sizeMm: d.size_mm || '',
          weightPerStone: d.weight_per_stone || 0,
          totalWeight: d.total_weight || 0,
          settingType: d.setting_type || 'Prong',
        }));
        setStones(newStones);
        fieldsApplied += newStones.length;
      }

      // Other stones
      if (parsed.other_stones?.length > 0 && stones.length === 0) {
        const otherStones = parsed.other_stones.map(s => ({
          id: crypto.randomUUID(),
          category: 'Other Stone',
          stoneCategory: 'Natural',
          shape: s.shape || 'Round',
          quality: s.stone_name || '',
          qty: s.pieces || 1,
          sizeMm: s.size_mm || '',
          weightPerStone: 0,
          totalWeight: s.weight_ct || 0,
          settingType: s.setting_type || 'Prong',
        }));
        setStones(prev => [...prev, ...otherStones]);
        fieldsApplied += otherStones.length;
      }

      toast.success(`AI filled ${fieldsApplied} fields. Review and adjust as needed.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI parsing failed');
    } finally { setAiParsing(false); }
  };

  // ─── Scroll-spy ───
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─── Weight calculations ───
  const calcNetWeight = () => {
    const total = metals.reduce((sum, m) => sum + (parseFloat(m.netWeight) || 0), 0);
    return total > 0 ? total.toFixed(2) : '';
  };

  const calcGrossWeight = () => {
    const totalMetal = metals.reduce((sum, m) => sum + (parseFloat(m.netWeight) || 0), 0);
    if (totalMetal === 0) return '';
    const totalStoneCt = stones.reduce((sum, s) => {
      return sum + (parseFloat(s.weight_per_stone) || 0) * (parseInt(s.quantity) || 0);
    }, 0);
    return (totalMetal + totalStoneCt / 5).toFixed(2);
  };

  const totalFindingsWeight = findings.reduce((s, f) => s + (parseFloat(f.weight) || 0), 0);
  const grossWtNum = parseFloat(formData.gross_weight) || 0;
  const totalWeight = grossWtNum > 0 ? (grossWtNum + totalFindingsWeight).toFixed(2) : '';

  useEffect(() => {
    const netWt = calcNetWeight();
    setFormData(prev => ({
      ...prev,
      net_weight: netWt,
      gross_weight: grossWeightManual ? prev.gross_weight : calcGrossWeight(),
    }));
  }, [metals, stones, grossWeightManual]);

  // ─── Metal CRUD ───
  const addMetal = () => {
    if (metals.length >= 3) return;
    setMetals(prev => [...prev, { id: crypto.randomUUID(), karat: '14K', color: 'Yellow', metalType: 'gold', netWeight: '' }]);
  };

  const updateMetal = (id, field, value) => {
    setMetals(prev => prev.map(m => {
      if (m.id !== id) return m;
      const updated = { ...m, [field]: value };
      if (field === 'metalType') {
        updated.karat = (METAL_KARATS[value] || [])[0] || '';
        updated.color = (METAL_COLOURS[value] || [])[0] || '';
      }
      return updated;
    }));
  };

  const removeMetal = (id) => {
    if (metals.length <= 1) return;
    setMetals(prev => prev.filter(m => m.id !== id));
  };

  // ─── Stone CRUD ───
  const addStone = () => {
    setStones(prev => [...prev, {
      id: crypto.randomUUID(), category: 'Accent Stone', stone_category: 'Natural', ownership: 'KS Goods', stone_shape: 'Round',
      stone_quality: '', setting_type: 'Prong', size_mm: '', weight_per_stone: '', quantity: 1, total_weight: 0,
    }]);
  };

  const updateStone = (idx, field, value) => {
    setStones(prev => prev.map((s, i) => {
      if (i !== idx) return s;
      const updated = { ...s, [field]: value };
      if (field === 'quantity' || field === 'weight_per_stone') {
        updated.total_weight = (parseFloat(updated.weight_per_stone) || 0) * (parseInt(updated.quantity) || 0);
      }
      return updated;
    }));
  };

  const removeStone = (idx) => setStones(prev => prev.filter((_, i) => i !== idx));

  const handleBulkStoneImport = (importedStones) => {
    setStones(importedStones);
    toast.success(`${importedStones.length} stones imported`);
  };

  // ─── Finding CRUD ───
  const pieceFindings = FINDINGS_BY_PIECE_TYPE[formData.piece_type] || [];

  const addFinding = () => {
    setFindings(prev => [...prev, { id: crypto.randomUUID(), name: '', code: '', weight: '', details: '' }]);
  };

  const updateFinding = (idx, field, value) => {
    setFindings(prev => prev.map((f, i) => {
      if (i !== idx) return f;
      const updated = { ...f, [field]: value };
      if (field === 'code') {
        const match = pieceFindings.find(pf => pf.code === value);
        if (match) {
          updated.name = match.name;
          const karat = metals[0]?.karat || '14K';
          updated.weight = match.weights?.[karat] || 0;
        }
      }
      return updated;
    }));
  };

  const removeFinding = (idx) => setFindings(prev => prev.filter((_, i) => i !== idx));

  const totalStoneWeight = stones.reduce((s, st) => s + (st.total_weight || 0), 0);
  const totalStonePcs = stones.reduce((s, st) => s + (parseInt(st.quantity) || 0), 0);

  // ─── Image upload ───
  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Use general file upload for CAD files, image upload for images
      const isImage = file.type.startsWith('image/');
      const res = isImage ? await uploadImage(file) : await uploadFile(file);
      const path = res.data.image || res.data.url || res.data.path;
      setFormData(prev => ({ ...prev, [field]: path }));
      toast.success('File uploaded');
    } catch (err) { console.error('Upload failed:', err); toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleRefImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadImage(file);
      setFormData(prev => ({
        ...prev, reference_images: [...prev.reference_images, res.data.image]
      }));
      toast.success('Reference image uploaded');
    } catch (err) { console.error('Ref upload failed:', err); toast.error('Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  // Style-No gate state — opened when handleSubmit fires with empty style_no.
  const [styleNoPromptOpen, setStyleNoPromptOpen] = useState(false);

  // ─── Submit ───
  const handleSubmit = async () => {
    if (!formData.client_name) {
      toast.error('Please fill Client Name');
      return;
    }
    await doSubmit(formData);
  };

  const handleStyleNoPromptSave = async (values) => {
    const styleNo = values.task;
    setStyleNoPromptOpen(false);
    setFormData(prev => ({ ...prev, style_no: styleNo }));
    await doSubmit({ ...formData, style_no: styleNo });
  };

  const doSubmit = async (data) => {
    setSaving(true);
    try {
      const primaryMetal = metals[0] || {};
      const extraMetals = metals.slice(1).map(m => ({
        metal_type: m.metalType ? m.metalType.charAt(0).toUpperCase() + m.metalType.slice(1) : 'Gold',
        metal_colour: m.color, metal_karat: m.karat, weight: parseFloat(m.netWeight) || null,
      }));

      const submitData = {
        ...data,
        metal_type: primaryMetal.metalType || '',
        metal_colour: primaryMetal.color || '',
        metal_karat: primaryMetal.karat || '',
        net_weight: data.net_weight ? parseFloat(data.net_weight) : null,
        gross_weight: data.gross_weight ? parseFloat(data.gross_weight) : null,
        metal_details: extraMetals,
        diamonds: stones,
        findings: findings.map(f => ({ name: f.name, custom_name: f.custom_name || '', code: f.code, weight: parseFloat(f.weight) || 0, details: f.details })),
        start_date: data.start_date ? new Date(data.start_date).toISOString() : null,
        target_date: data.target_date ? new Date(data.target_date).toISOString() : null,
        quantity: data.quantity ? parseInt(data.quantity) : null,
        cad_points: data.cad_points ? parseFloat(data.cad_points) : null,
        design_points: data.design_points ? parseFloat(data.design_points) : null,
        cad_difficulty: data.cad_difficulty || 'moderate',
        cad_start_time: data.cad_start_time ? new Date(data.cad_start_time).toISOString() : null,
      };

      const response = await createTask(submitData);
      toast.success('Task created successfully!');
      navigate(`/tasks?selected=${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally { setSaving(false); }
  };

  const handleReset = () => {
    setMetals([{ id: crypto.randomUUID(), karat: '14K', color: 'Yellow', metalType: 'gold', netWeight: '' }]);
    setStones([]); setFindings([]); setGrossWeightManual(false);
    setFormData({
      party_cad_no: '', style_no: '', ks_design_no: '', client_name: '', product_name: '',
      piece_type: 'ring', size: '', quantity: '', priority: 'normal', deadline: '',
      collection_name: '', cad_file_name: '', designer_name: '', designer_hashtag: '', designer_id: '', design_points: '',
      cad_url: '', net_weight: '', gross_weight: '',
      finishing: '', rhodium_plating: '', engraving: '', special_instructions: '',
      stamping_instructions: '', chain_assembling: '', other_details: '',
      stage: '', start_date: new Date().toISOString().split('T')[0], target_date: '',
      notes: '', product_image: '', cad_file: '', reference_images: [], stamping_images: [],
    });
    toast.success('All fields reset');
  };

  const imagePreviewUrl = formData.product_image ? getImageUrl(formData.product_image) : null;

  return (
    <CollapsibleLayout>
      <div className="flex flex-col h-[calc(100vh-48px)] bg-background" data-testid="create-task-page">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-card/80 backdrop-blur-sm flex-shrink-0">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')} className="h-7 text-xs" data-testid="back-btn">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />Back
            </Button>
            <div>
              <h1 className="text-base font-bold text-primary tracking-wide">Create New CAD File</h1>
              <p className="text-[10px] text-muted-foreground">Enter all details for the new design</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowAiBox(!showAiBox)} className={`h-7 text-xs ${showAiBox ? 'bg-violet-50 border-violet-300 text-violet-700' : ''}`} data-testid="ai-mode-toggle">
              <Sparkles className="h-3.5 w-3.5 mr-1" />AI Mode
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/tasks')} className="h-7 text-xs">
              <X className="h-3.5 w-3.5 mr-1" />Cancel
            </Button>
            <Button size="sm" onClick={handleSubmit} disabled={saving} className="h-7 text-xs bg-primary text-primary-foreground hover:bg-primary/90" data-testid="save-task-btn">
              <Save className="h-3.5 w-3.5 mr-1" />{saving ? 'Creating...' : 'Create Task'}
            </Button>
          </div>
        </div>

        {/* AI Instruction Box */}
        {showAiBox && (
          <div className="mx-4 mt-2 mb-1 p-3 rounded-lg border border-violet-200 bg-violet-50/50" data-testid="ai-instruction-box">
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <Label className="text-xs font-semibold text-violet-700 flex items-center gap-1">
                  <Sparkles size={12} /> Paste raw instructions — AI will extract fields
                </Label>
                <Textarea
                  value={aiInstruction}
                  onChange={e => setAiInstruction(e.target.value)}
                  placeholder="e.g. 18k yellow gold solitaire ring, 1ct oval diamond, halo setting, comfort fit band, size 7, for client DAI01, party cad RING-2025..."
                  rows={3}
                  className="mt-1.5 text-sm bg-white border-violet-200 focus:border-violet-400"
                  data-testid="ai-instruction-input"
                />
                <p className="text-[10px] text-violet-500 mt-1">AI will only fill empty fields — your existing entries are preserved.</p>
              </div>
              <Button
                onClick={handleAiParse}
                disabled={aiParsing || !aiInstruction.trim()}
                className="mt-5 bg-violet-600 hover:bg-violet-700 text-white h-9 px-4 text-xs"
                data-testid="ai-parse-btn"
              >
                {aiParsing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
                {aiParsing ? 'Parsing...' : 'Parse & Fill'}
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Sidebar */}
          <div className="w-40 bg-muted/30 border-r border-border flex-shrink-0 py-3 px-2 overflow-y-auto">
            <nav className="space-y-0.5">
              {SECTIONS.filter(s => !(user?.role === 'customer' && s.id === 'workflow')).map((section) => (
                <button key={section.id} onClick={() => scrollToSection(section.id)}
                  className={`w-full text-left px-2.5 py-1.5 rounded text-xs transition-colors ${
                    activeSection === section.id
                      ? 'bg-card shadow-sm font-semibold text-primary border-l-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground hover:bg-card/50'
                  }`} data-testid={`nav-${section.id}`}>
                  {section.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Form Content */}
          <div className="flex-1 overflow-y-auto p-4">
            <div className="max-w-3xl mx-auto space-y-5">

              {/* ═══ Basic Info ═══ */}
              <div id="basic" ref={el => sectionRefs.current.basic = el}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Client Name *</Label>
                    {user?.role === 'customer' ? (
                      <Input
                        disabled
                        value={formData.client_name}
                        className="w-full h-8 mt-1 bg-muted cursor-not-allowed text-sm"
                      />
                    ) : (
                      <ClientSelect 
                        value={formData.client_name} 
                        onChange={async (v) => {
                          setFormData(p => ({ ...p, client_name: v, ks_design_no: '' }));
                          if (v) {
                            try {
                              const res = await getNextKsDesignNo(v);
                              setFormData(p => ({ ...p, client_name: v, ks_design_no: res.data.next_no }));
                            } catch (err) {
                              toast.error('Failed to generate KS Design No');
                            }
                          }
                        }} 
                        placeholder="Select client..." 
                        className="w-full h-8 mt-1" 
                      />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">KS Design No</Label>
                    <Input
                      disabled
                      value={formData.ks_design_no || ''}
                      placeholder="Auto-generated"
                      className="w-full h-8 mt-1 bg-muted cursor-not-allowed text-sm font-mono font-semibold"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Party CAD No</Label>
                    <Input value={formData.party_cad_no} onChange={e => setFormData(p => ({ ...p, party_cad_no: e.target.value }))} placeholder="PARTY-001" className="h-8 text-sm mt-1" data-testid="party-cad-input" />
                  </div>
                  <div>
                    <Label className="text-xs">Style No</Label>
                    <Input value={formData.style_no} onChange={e => setFormData(p => ({ ...p, style_no: e.target.value }))} placeholder="e.g. R2-216680" className="h-8 text-sm mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Piece Type *</Label>
                    <Select value={formData.piece_type} onValueChange={v => setFormData(p => ({ ...p, piece_type: v }))}>
                      <SelectTrigger className="h-8 text-sm mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{PIECE_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Product Name</Label>
                    <Input value={formData.product_name} onChange={e => setFormData(p => ({ ...p, product_name: e.target.value }))} className="h-8 text-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Size</Label>
                    <Input value={formData.size} onChange={e => setFormData(p => ({ ...p, size: e.target.value }))} placeholder="e.g. 7, M, 18 inch" className="h-8 text-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Quantity</Label>
                    <Input type="number" value={formData.quantity} onChange={e => setFormData(p => ({ ...p, quantity: e.target.value }))} placeholder="1" className="h-8 text-sm mt-1" data-testid="quantity-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Priority</Label>
                    <Select value={formData.priority} onValueChange={v => setFormData(p => ({ ...p, priority: v }))}>
                      <SelectTrigger className="h-8 text-sm mt-1" data-testid="priority-input"><SelectValue /></SelectTrigger>
                      <SelectContent>{PRIORITY_LEVELS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">CAD Difficulty</Label>
                    <Select value={formData.cad_difficulty} onValueChange={v => {
                      setFormData(p => {
                        const diff = CAD_DIFFICULTY_LEVELS.find(d => d.value === v);
                        const wt = parseFloat(p.net_weight || p.gross_weight) || 5;
                        const autoPoints = diff ? Math.round(wt * diff.multiplier * 10) / 10 : p.cad_points;
                        return { ...p, cad_difficulty: v, cad_points: p.cad_points ? p.cad_points : String(autoPoints) };
                      });
                    }} disabled={isExecutorOrFinalExecutor}>
                      <SelectTrigger className="h-8 text-sm mt-1" data-testid="cad-difficulty-input"><SelectValue /></SelectTrigger>
                      <SelectContent>{CAD_DIFFICULTY_LEVELS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">CAD Points (hrs)</Label>
                    <Input type="number" step="0.1" value={formData.cad_points} onChange={e => setFormData(p => ({ ...p, cad_points: e.target.value }))} placeholder="Auto or manual" className="h-8 text-sm mt-1" data-testid="cad-points-input" disabled={isExecutorOrFinalExecutor} />
                  </div>
                  <div>
                    <Label className="text-xs">CAD File Name</Label>
                    <Input value={formData.cad_file_name} onChange={e => setFormData(p => ({ ...p, cad_file_name: e.target.value }))} placeholder="e.g. RG-001.3dm" className="h-8 text-sm mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Collection</Label>
                    <Input value={formData.collection_name} onChange={e => setFormData(p => ({ ...p, collection_name: e.target.value }))} className="h-8 text-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">CAD Link</Label>
                    <Input value={formData.cad_url} onChange={e => setFormData(p => ({ ...p, cad_url: e.target.value }))} placeholder="https://..." className="h-8 text-sm mt-1" data-testid="cad-url-input" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Executor (Designer)</Label>
                    <Select value={formData.designer_id || ''} onValueChange={v => {
                      const found = executors.find(ex => ex.id === v);
                      setFormData(p => ({ ...p, designer_id: v, designer_name: found ? found.name : '' }));
                    }} disabled={isExecutorOrFinalExecutor}>
                      <SelectTrigger className="h-8 text-sm mt-1" data-testid="designer-select"><SelectValue placeholder="Select executor..." /></SelectTrigger>
                      <SelectContent>
                        {executors.map(ex => (
                          <SelectItem key={ex.id} value={ex.id}>{ex.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Design Points</Label>
                    <Input type="number" value={formData.design_points || ''} onChange={e => setFormData(p => ({ ...p, design_points: e.target.value }))} placeholder="Points" className="h-8 text-sm mt-1" data-testid="design-points-input" disabled={isExecutorOrFinalExecutor} />
                  </div>
                  <div>
                    <Label className="text-xs">CAD File URL</Label>
                    <Input value={formData.cad_url || ''} onChange={e => setFormData(p => ({ ...p, cad_url: e.target.value }))} placeholder="Z:\CAD Files\..." className="h-8 text-sm mt-1" data-testid="cad-url-input" />
                  </div>
                </div>
              </div>

              {/* ═══ Metal(s) & Weights ═══ */}
              <div id="metal" ref={el => sectionRefs.current.metal = el} className="border border-border rounded-lg p-3 bg-card/50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold">Metal(s) & Weights</Label>
                  {metals.length < 3 && (
                    <Button variant="outline" size="sm" onClick={addMetal} className="h-6 text-xs px-2">
                      <Plus className="h-3 w-3 mr-1" /> Add Metal
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {metals.map((metal) => {
                    const karatOptions = METAL_KARATS[metal.metalType] || ['14K'];
                    const colorOptions = METAL_COLOURS[metal.metalType] || ['Yellow'];
                    return (
                      <div key={metal.id} className="flex items-center gap-2">
                        <Select value={metal.metalType} onValueChange={v => updateMetal(metal.id, 'metalType', v)}>
                          <SelectTrigger className="h-7 text-xs w-[90px]"><SelectValue /></SelectTrigger>
                          <SelectContent>{METAL_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={metal.karat} onValueChange={v => updateMetal(metal.id, 'karat', v)}>
                          <SelectTrigger className="h-7 text-xs w-[80px]"><SelectValue /></SelectTrigger>
                          <SelectContent>{karatOptions.map(k => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={metal.color} onValueChange={v => updateMetal(metal.id, 'color', v)}>
                          <SelectTrigger className="h-7 text-xs w-[90px]"><SelectValue /></SelectTrigger>
                          <SelectContent>{colorOptions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                        <Input className="h-7 text-xs w-[100px]" value={metal.netWeight} onChange={e => updateMetal(metal.id, 'netWeight', e.target.value)} placeholder="Net Wt (gms)" type="number" step="0.01" />
                        {metals.length > 1 && (
                          <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => removeMetal(metal.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="grid grid-cols-3 gap-2 mt-3 pt-2 border-t border-border/50">
                  <div>
                    <Label className="text-xs text-muted-foreground">Net Weight (gms)</Label>
                    <div className="text-sm font-medium text-foreground">{formData.net_weight || '\u2014'}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Gross Wt (gms)</Label>
                    <Input value={formData.gross_weight} onChange={e => { setGrossWeightManual(true); setFormData(p => ({ ...p, gross_weight: e.target.value })); }}
                      placeholder="Auto-calc" className="h-7 text-xs" type="number" step="0.01" />
                    <div className="flex items-center gap-1.5 mt-1">
                      <Checkbox id="grossAutoCreate" checked={!grossWeightManual}
                        onCheckedChange={(checked) => { setGrossWeightManual(!checked); if (checked) setFormData(p => ({ ...p, gross_weight: calcGrossWeight() })); }}
                        className="h-3 w-3" />
                      <label htmlFor="grossAutoCreate" className="text-[10px] text-muted-foreground cursor-pointer">Auto-calculate</label>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Total Wt (gms)</Label>
                    <div className="text-sm font-bold text-primary">{totalWeight || '\u2014'}</div>
                    <p className="text-[9px] text-muted-foreground">Gross + Findings</p>
                  </div>
                </div>
              </div>

              {/* ═══ Stone Details ═══ */}
              <div id="stones" ref={el => sectionRefs.current.stones = el} className="border border-border rounded-lg p-3 bg-card/50">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <Label className="text-xs font-semibold">Stone Details</Label>
                    {stones.length > 0 && (
                      <span className="text-[10px] text-muted-foreground ml-2">Total: {totalStonePcs} pcs, {totalStoneWeight.toFixed(2)} ct</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="outline" size="sm" onClick={() => setBulkStoneOpen(true)} className="h-7 text-xs" data-testid="bulk-add-stone-btn">
                      <Grid className="h-3 w-3 mr-1" /> Bulk Add
                    </Button>
                    <Button variant="outline" size="sm" onClick={addStone} className="h-7 text-xs" data-testid="add-stone-btn">
                      <Plus className="h-3 w-3 mr-1" /> Add Stone
                    </Button>
                  </div>
                </div>
                {stones.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No stones added yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-border">
                          {['Stone', 'Ownership', 'Cut', 'Category', 'Quality', 'Size', 'Ct. Wt', 'Qty', 'Total', ''].map(h => (
                            <th key={h} className="text-left py-1 px-1 font-medium text-muted-foreground">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {stones.map((st, idx) => (
                          <tr key={st.id} className="border-b border-border/30">
                            <td className="py-0.5 px-0.5">
                              {['Natural', 'Lab', 'CVD', 'Colour Stone'].includes(st.stone_category) ? (
                                <Select value={st.stone_category || 'Natural'} onValueChange={v => { if (v === '__other') updateStone(idx, 'stone_category', ''); else updateStone(idx, 'stone_category', v); }}>
                                  <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[78px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    {['Natural', 'Lab', 'CVD', 'Colour Stone'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                                    <SelectItem value="__other">Other...</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input className="h-6 text-[11px] p-1 w-[78px]" value={st.stone_category || ''} onChange={e => updateStone(idx, 'stone_category', e.target.value)} placeholder="Type..." />
                              )}
                            </td>
                            <td className="py-0.5 px-0.5">
                              {['KS Goods', 'Party Goods'].includes(st.ownership) || !st.ownership ? (
                                <Select value={st.ownership || 'KS Goods'} onValueChange={v => { if (v === '__other') updateStone(idx, 'ownership', ''); else updateStone(idx, 'ownership', v); }}>
                                  <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[78px]"><SelectValue /></SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="KS Goods">KS Goods</SelectItem>
                                    <SelectItem value="Party Goods">Party Goods</SelectItem>
                                    <SelectItem value="__other">Other...</SelectItem>
                                  </SelectContent>
                                </Select>
                              ) : (
                                <Input className="h-6 text-[11px] p-1 w-[78px]" value={st.ownership || ''} onChange={e => updateStone(idx, 'ownership', e.target.value)} placeholder="Type..." />
                              )}
                            </td>
                            <td className="py-0.5 px-0.5">
                              {STONE_SHAPES.includes(st.stone_shape) ? (
                                <Select value={st.stone_shape || 'Round'} onValueChange={v => { if (v === '__other') updateStone(idx, 'stone_shape', ''); else updateStone(idx, 'stone_shape', v); }}>
                                  <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[68px]"><SelectValue placeholder="Shape" /></SelectTrigger>
                                  <SelectContent>{STONE_SHAPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}<SelectItem value="__other">Other...</SelectItem></SelectContent>
                                </Select>
                              ) : (
                                <Input className="h-6 text-[11px] p-1 w-[68px]" value={st.stone_shape || ''} onChange={e => updateStone(idx, 'stone_shape', e.target.value)} placeholder="Cut..." />
                              )}
                            </td>
                            <td className="py-0.5 px-0.5">
                              <Select value={st.category || 'Accent Stone'} onValueChange={v => updateStone(idx, 'category', v)}>
                                <SelectTrigger className="h-6 text-[11px] border-0 p-1 w-[70px]"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Center Stone">Center</SelectItem>
                                  <SelectItem value="Accent Stone">Accent</SelectItem>
                                  <SelectItem value="Side Stone">Side</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-16" value={st.stone_quality || ''} onChange={e => updateStone(idx, 'stone_quality', e.target.value)} placeholder="VS1" /></td>
                            <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" value={st.size_mm || ''} onChange={e => updateStone(idx, 'size_mm', e.target.value)} placeholder="mm" /></td>
                            <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-14" type="number" step="0.001" value={st.weight_per_stone || ''} onChange={e => updateStone(idx, 'weight_per_stone', e.target.value)} placeholder="ct" /></td>
                            <td className="py-0.5 px-0.5"><Input className="h-6 text-[11px] p-1 w-12" type="number" value={st.quantity || ''} onChange={e => updateStone(idx, 'quantity', parseInt(e.target.value) || 0)} /></td>
                            <td className="py-0.5 px-0.5 text-center text-[11px] text-muted-foreground">{(st.total_weight || 0).toFixed(2)}</td>
                            <td>
                              <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive" onClick={() => removeStone(idx)}>
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* ═══ Findings ═══ */}
              <div id="findings" ref={el => sectionRefs.current.findings = el} className="border border-border rounded-lg p-3 bg-card/50">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold">
                    Findings ({PIECE_TYPES.find(p => p.value === formData.piece_type)?.label || formData.piece_type})
                    {findings.length > 0 && <span className="text-[10px] text-muted-foreground ml-2">{findings.length} items | {totalFindingsWeight.toFixed(2)}g</span>}
                  </Label>
                  <Button variant="outline" size="sm" onClick={addFinding} className="h-6 text-xs px-2" data-testid="add-finding-btn">
                    <Plus className="h-3 w-3 mr-1" /> Add Finding
                  </Button>
                </div>
                {findings.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No findings added yet.</p>
                ) : (
                  findings.map((finding, idx) => (
                    <div key={finding.id} className="flex items-center gap-2 mb-1.5">
                      <Select value={finding.code || '__none'} onValueChange={v => updateFinding(idx, 'code', v === '__none' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs flex-1"><SelectValue placeholder="Select type..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">Select type...</SelectItem>
                          {pieceFindings.filter(pf => pf.code !== 'NONE').map(pf => (
                            <SelectItem key={pf.code} value={pf.code}>{pf.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input className="h-7 text-xs w-[80px]" value={finding.custom_name || ''} onChange={e => updateFinding(idx, 'custom_name', e.target.value)} placeholder="Name (e.g. ANC30)" />
                      <Input className="h-7 text-xs w-[80px]" value={finding.weight || ''} onChange={e => updateFinding(idx, 'weight', e.target.value)} placeholder="Weight" type="number" step="0.01" />
                      <Input className="h-7 text-xs w-[80px]" value={finding.details || ''} onChange={e => updateFinding(idx, 'details', e.target.value)} placeholder="Details" />
                      <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => removeFinding(idx)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))
                )}
              </div>

              {/* ═══ Finishing & Engraving ═══ */}
              <div id="finishing" ref={el => sectionRefs.current.finishing = el}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Finishing</Label>
                    <Select
                      value={FINISHING_TYPES.includes(formData.finishing) ? formData.finishing : (formData.finishing ? '__custom' : '__none')}
                      onValueChange={v => {
                        if (v === '__custom') setFormData(p => ({ ...p, finishing: '' }));
                        else if (v === '__none') setFormData(p => ({ ...p, finishing: '' }));
                        else setFormData(p => ({ ...p, finishing: v }));
                      }}
                    >
                      <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select finishing..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">None</SelectItem>
                        {FINISHING_TYPES.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                        <SelectItem value="__custom">Other...</SelectItem>
                      </SelectContent>
                    </Select>
                    {formData.finishing && !FINISHING_TYPES.includes(formData.finishing) && (
                      <Input className="h-7 text-xs mt-1" value={formData.finishing} onChange={e => setFormData(p => ({ ...p, finishing: e.target.value }))} placeholder="Specify finishing..." />
                    )}
                  </div>
                  <div>
                    <Label className="text-xs">Rhodium Plating</Label>
                    <Select value={formData.rhodium_plating || '__none'} onValueChange={v => setFormData(p => ({ ...p, rhodium_plating: v === '__none' ? '' : v }))}>
                      <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none">None</SelectItem>
                        <SelectItem value="Full Rhodium">Full Rhodium</SelectItem>
                        <SelectItem value="Partial Rhodium">Partial Rhodium</SelectItem>
                        <SelectItem value="Black Rhodium">Black Rhodium</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs">Engraving Instructions</Label>
                  <Textarea value={formData.engraving} onChange={e => setFormData(p => ({ ...p, engraving: e.target.value }))} placeholder="Engraving text or instructions..." rows={2} className="text-sm mt-1" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <div>
                    <Label className="text-xs">Stamping Instructions</Label>
                    <Input value={formData.stamping_instructions} onChange={e => setFormData(p => ({ ...p, stamping_instructions: e.target.value }))} placeholder="e.g. K's 750" className="h-8 text-sm mt-1" />
                  </div>
                  <div>
                    <Label className="text-xs">Chain / Assembling</Label>
                    <Input value={formData.chain_assembling} onChange={e => setFormData(p => ({ ...p, chain_assembling: e.target.value }))} placeholder="e.g. 18 inch cable chain" className="h-8 text-sm mt-1" />
                  </div>
                </div>
                <div className="mt-3">
                  <Label className="text-xs">Special Instructions</Label>
                  <Textarea value={formData.special_instructions} onChange={e => setFormData(p => ({ ...p, special_instructions: e.target.value }))} placeholder="Any special requirements..." rows={2} className="text-sm mt-1" />
                </div>
              </div>

              {/* ═══ Images ═══ */}
              <div id="images" ref={el => sectionRefs.current.images = el} className="border border-border rounded-lg p-3 bg-card/50 space-y-4">
                <Label className="text-xs font-semibold block">Files & Images</Label>

                {/* CAD File (Primary) + CAD Link */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">CAD File (Primary .3dm/.stl)</Label>
                    <div className="flex items-start gap-2 mt-1">
                      <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
                        <Upload className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{uploading ? 'Uploading...' : 'Upload CAD file'}</span>
                        <input type="file" className="hidden" onChange={e => handleImageUpload(e, 'cad_file')} disabled={uploading} />
                      </label>
                      {formData.cad_file && (
                        <div className="flex items-center gap-1 text-xs text-primary">
                          <a href={getImageUrl(formData.cad_file)} target="_blank" rel="noopener noreferrer" className="underline truncate max-w-[120px]">View CAD</a>
                          <Button variant="ghost" size="icon" className="h-5 w-5 text-muted-foreground hover:text-destructive" onClick={() => setFormData(p => ({ ...p, cad_file: '' }))}><X className="h-3 w-3" /></Button>
                        </div>
                      )}
                    </div>
                    {formData.cad_url && <a href={formData.cad_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-primary underline mt-1 block truncate">CAD Link: {formData.cad_url}</a>}
                  </div>

                  {/* Product (Hero) Image — CAD Image / Main Reference */}
                  <div>
                    <Label className="text-xs text-muted-foreground">CAD Image (Main Reference)</Label>
                    <div className="flex items-start gap-2 mt-1">
                      <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
                        <Upload className="h-4 w-4 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">{uploading ? 'Uploading...' : 'Upload image'}</span>
                        <input type="file" accept="image/*" className="hidden" onChange={e => handleImageUpload(e, 'product_image')} disabled={uploading} />
                      </label>
                      {imagePreviewUrl && (
                        <div className="relative w-20 h-20 border border-border rounded-lg overflow-hidden" data-testid="image-preview">
                          <img src={imagePreviewUrl} alt="CAD" className="w-full h-full object-contain bg-white" />
                          <Button variant="ghost" size="icon" className="absolute top-0 right-0 h-5 w-5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground" onClick={() => setFormData(p => ({ ...p, product_image: '' }))}>
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Stamping Reference Images */}
                <div>
                  <Label className="text-xs text-muted-foreground">Stamping Reference Images (optional)</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {(formData.stamping_images || []).map((img, i) => (
                      <div key={`stamp-${img}-${i}`} className="relative w-20 h-20 border border-amber-300 rounded-lg overflow-hidden">
                        <img src={getImageUrl(img)} alt={`Stamp ${i + 1}`} className="w-full h-full object-contain bg-white" />
                        <Button variant="ghost" size="icon" className="absolute top-0 right-0 h-5 w-5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground" onClick={() => setFormData(p => ({ ...p, stamping_images: (p.stamping_images || []).filter((_, j) => j !== i) }))}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    {(formData.stamping_images || []).length < 5 && (
                      <label className="flex flex-col items-center justify-center w-20 h-20 border border-dashed border-amber-300 rounded-lg cursor-pointer hover:bg-amber-50/30 transition-colors">
                        <Upload className="h-3 w-3 text-amber-500" />
                        <span className="text-[9px] text-amber-600 mt-0.5">Stamping</span>
                        <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                          const file = e.target.files?.[0]; if (!file) return;
                          setUploading(true);
                          try {
                            const res = await uploadImage(file);
                            const path = res.data.image || res.data.url || res.data.path;
                            setFormData(p => ({ ...p, stamping_images: [...(p.stamping_images || []), path] }));
                            toast.success('Stamping image uploaded');
                          } catch { toast.error('Upload failed'); }
                          finally { setUploading(false); }
                        }} disabled={uploading} />
                      </label>
                    )}
                  </div>
                </div>

                {/* Reference Images (Supporting) */}
                <div>
                  <Label className="text-xs text-muted-foreground">Customer Reference Images</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {formData.reference_images.map((img, i) => (
                      <div key={`ref-${img}-${i}`} className="relative w-20 h-20 border border-border rounded-lg overflow-hidden">
                        <img src={getImageUrl(img)} alt={`Ref ${i + 1}`} className="w-full h-full object-contain bg-white" />
                        <Button variant="ghost" size="icon" className="absolute top-0 right-0 h-5 w-5 bg-background/80 hover:bg-destructive hover:text-destructive-foreground" onClick={() => setFormData(p => ({ ...p, reference_images: p.reference_images.filter((_, j) => j !== i) }))}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                    {formData.reference_images.length < 5 && (
                      <label className="flex flex-col items-center justify-center w-20 h-20 border border-dashed border-border rounded-lg cursor-pointer hover:bg-muted/30 transition-colors">
                        <Upload className="h-3 w-3 text-muted-foreground" />
                        <span className="text-[9px] text-muted-foreground mt-0.5">Reference</span>
                        <input type="file" accept="image/*" className="hidden" onChange={handleRefImageUpload} disabled={uploading} />
                      </label>
                    )}
                  </div>
                </div>
              </div>

              {/* ═══ Other Details ═══ */}
              <div id="other" ref={el => sectionRefs.current.other = el}>
                <Label className="text-xs">Other Details</Label>
                <Textarea value={formData.other_details} onChange={e => setFormData(p => ({ ...p, other_details: e.target.value }))} placeholder="Any other details about this design..." rows={3} className="text-sm mt-1" />
              </div>

              {/* ═══ Workflow & Notes ═══ */}
              {user?.role !== 'customer' && (
                <div id="workflow" ref={el => sectionRefs.current.workflow = el}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-xs">Stage</Label>
                      <Select value={formData.stage || '__none'} onValueChange={v => setFormData(p => ({ ...p, stage: v === '__none' ? '' : v }))}>
                        <SelectTrigger className="h-8 text-sm mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none">Select</SelectItem>
                          {STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Start Date</Label>
                      <Input type="date" value={formData.start_date} onChange={e => setFormData(p => ({ ...p, start_date: e.target.value }))} className="h-8 text-sm mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs">Deadline</Label>
                      <Input type="date" value={formData.target_date} onChange={e => setFormData(p => ({ ...p, target_date: e.target.value }))} className="h-8 text-sm mt-1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                    <div>
                      <Label className="text-xs">End Date</Label>
                      <Input type="date" value="" className="h-8 text-sm mt-1 bg-muted/50" disabled />
                      <p className="text-[9px] text-muted-foreground mt-0.5">Auto-set when sent to client</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs">Notes</Label>
                    <Textarea value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes..." rows={2} className="text-sm mt-1" />
                  </div>
                </div>
              )}

              {/* Reset */}
              <Button variant="outline" size="sm" onClick={handleReset} className="w-full text-xs text-muted-foreground" data-testid="reset-btn">
                <RotateCcw className="h-3 w-3 mr-1" /> Reset All Fields
              </Button>

            </div>
          </div>
        </div>
      </div>

      <TaskStoneImportDialog open={bulkStoneOpen} onOpenChange={setBulkStoneOpen} onImport={handleBulkStoneImport} existingStones={stones} />
      <StyleNoPromptDialog
        open={styleNoPromptOpen}
        items={[{ key: 'task', label: formData.product_name || formData.party_cad_no || 'this task' }]}
        title="Style No required to create task"
        onSave={handleStyleNoPromptSave}
        onCancel={() => setStyleNoPromptOpen(false)}
      />
    </CollapsibleLayout>
  );
};

export default CreateTaskPage;
